import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Grid, TextField, InputAdornment, MenuItem } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useAdmin } from '../context/AppContext';

const Cart = () => {
    const { cartItems, removeFromCart } = useCart();
    const [showForm, setShowForm] = useState(false);
    const { adminProfile } = useAdmin();
    const [formData, setFormData] = useState({
        name: '',
        place: '',
        occasion: '',
        eventDate: '',
        eventTime: '',
        pax: '',
        scheduleType: '',
        venue: '',
        orderNote: ''
    });
    const [formErrors, setFormErrors] = useState({});

    const handleNextClick = () => {
        setShowForm(true);
    };


    const validateField = (name, value) => {
        let error = '';
        if (name !== 'orderNote' && !value.trim()) {  // Validate all fields except 'orderNote'
            error = 'This field is required.';  // Set error message for required fields
        }
        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value); // Validate the field on change
        // Update errors
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: error,
        }));
        // Update form data
        setFormData((prevData) => {
            const updatedData = { ...prevData, [name]: value };
            // Save updated data to cookies
            // Exclude date and time fields from cookies
            const { eventDate, eventTime, ...dataToStore } = updatedData;

            // Save only required fields to cookies
            document.cookie = `formData=${JSON.stringify(dataToStore)}; path=/; max-age=604800;`;
            return updatedData;
        });
    }
    // Load saved form data from cookies
    useEffect(() => {
        const getCookie = (cookieName) => {
            const cookies = document.cookie.split('; ').reduce((acc, current) => {
                const [key, value] = current.split('=');
                acc[key] = value;
                return acc;
            }, {});
            return cookies[cookieName] ? JSON.parse(decodeURIComponent(cookies[cookieName])) : null;
        };

        const savedFormData = getCookie('formData');
        if (savedFormData) {
            setFormData(savedFormData);
        }
    }, []);

    const clearFormCookies = () => {
        document.cookie = 'formData=; path=/; max-age=0;';
    };
    // Function to check if the entire form is valid (all required fields are filled):
    const isFormValid = () => {
        const errors = {};
        Object.keys(formData).forEach((key) => {
            if (key !== 'orderNote') {  // Skip validation for 'orderNote' field
                const error = validateField(key, formData[key]);  // Validate required fields
                if (error) errors[key] = error;  // Add errors to the errors object
            }
        });
        setFormErrors(errors);  // Update formErrors state
        return Object.keys(errors).length === 0;  // Return true if no errors, else false
    };


    const handleWhatsAppOrder = () => {
        if (!isFormValid()) {
            return;
        }
        if (!adminProfile || !adminProfile.whatsapp) {
            console.error("Admin profile or WhatsApp number is not available.");
            return;
        }

        // Group items based on the dynamic category/sub-category condition
        const groupedItems = cartItems.reduce((acc, item) => {
            const groupKey = item.category_id === 2 ? "SADYA" : item.sub_category_name; // Group by SADYA or sub_category_name
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(item.name); // Add item name to the respective group
            return acc;
        }, {});

        // Generate the message
        const message = `
    Name: ${formData.name}
    Place: ${formData.place}
    Occasion: ${formData.occasion}
    Event Date: ${formData.eventDate} 📅
    Event Time: ${formData.eventTime} 🕔
    Pax: ${formData.pax} 👨‍👧‍👧
    Schedule Type: ${formData.scheduleType}
    Venue: ${formData.venue}
    Order Note: 📝${formData.orderNote}
    
    ${Object.entries(groupedItems)
                .map(([groupName, items]) =>
                    `*${groupName.toUpperCase()}*\n----------------------------------\n${items.map((item, index) => `${index + 1}) ${item}`).join('\n')}`
                )
                .join('\n\n')}
        `.trim();

        // Encode and open WhatsApp URL
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${adminProfile?.whatsapp}?text=${encodedMessage}`;
        window.open(whatsappUrl, '_blank');

        // Clear the cart after sending the message
        cartItems.forEach((item) => {
            removeFromCart(item.product_id); // Call removeFromCart for each item
        });
    };



    return (
        <Box sx={{ padding: 2, maxWidth: 400, margin: 'auto' }}>
            <Typography variant="h5" color="primary" fontWeight="700" textAlign="center" mb={2}>
                Checkout
            </Typography>
            <hr />

            {/* Cart Items List */}
            {cartItems.length === 0 ? (
                <Typography variant="subtitle1" align="center" mt={2}>Your cart is empty</Typography>
            ) : (
                <Box mt={2}>
                    <Typography variant="subtitle1" mb={1}>Shopping Cart ({cartItems.length})</Typography>
                    {cartItems.map((item) => (
                        <Box
                            key={item.product_id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                                p: 1,
                                border: '1px solid #ddd',
                                borderRadius: 1,
                            }}
                        >
                            <img
                                src={item.image}
                                alt={item.name}
                                style={{ width: 50, height: 50, borderRadius: 4 }}
                            />
                            <Typography variant="body1" sx={{ flex: 1, mx: 2 }}>{item.name}</Typography>
                            <IconButton onClick={() => removeFromCart(item.product_id)} color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}

                    {!showForm && (
                        <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={handleNextClick}
                            sx={{ mt: 2, backgroundColor: (theme) => theme.palette.primary.main }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            )}

            {/* Form Section */}
            {showForm && cartItems.length > 0 && (
                <Box mt={2} component="form">
                    <Grid container spacing={2}>
                        {[
                            { label: 'Name', name: 'name', placeholder: 'Name' },
                            { label: 'Place', name: 'place', placeholder: 'Place' },
                            { label: 'Event Type/Style', name: 'occasion', placeholder: 'E.g., Marriage, Product Launch' },
                            { label: 'Venue', name: 'venue', placeholder: 'Venue' },
                        ].map((field, index) => (
                            <Grid item xs={12} key={index}>
                                <TextField
                                    fullWidth
                                    label={field.label}
                                    name={field.name}
                                    value={formData[field.name]}
                                    onChange={handleInputChange}
                                    error={!!formErrors.name}  // Show error if validation failed for this field
                                    helperText={formErrors.name}  // Show error message
                                    variant="outlined"
                                    size="small"
                                    placeholder={field.placeholder}
                                    multiline
                                    rows={1.5}
                                    InputProps={{
                                        style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                    }}

                                />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Event Date"
                                name="eventDate"
                                value={formData.eventDate}
                                onChange={handleInputChange}
                                error={!!formErrors.eventDate}  // Show error if validation failed for this field
                                helperText={formErrors.eventDate}  // Show error message
                                type="date"
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Event Time"
                                name="eventTime"
                                value={formData.eventTime}
                                onChange={handleInputChange}
                                error={!!formErrors.eventTime}  // Show error if validation failed for this field
                                helperText={formErrors.eventTime}  // Show error message
                                type="time"
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">🕒</InputAdornment>
                                    ),
                                    style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Schedule Type"
                                name="scheduleType"
                                value={formData.scheduleType}
                                onChange={handleInputChange}
                                error={!!formErrors.scheduleType}  // Show error if validation failed for this field
                                helperText={formErrors.scheduleType}  // Show error message
                                select
                                variant="outlined"
                                size="small"
                                multiline
                                rows={1.5}
                                InputProps={{
                                    style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                }}
                            >
                                <MenuItem value="">Select Schedule Type</MenuItem>
                                <MenuItem value="Breakfast">Breakfast</MenuItem>
                                <MenuItem value="Lunch">Lunch</MenuItem>
                                <MenuItem value="Dinner">Dinner</MenuItem>

                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Person Count"
                                name="pax"
                                value={formData.pax}
                                onChange={handleInputChange}
                                error={!!formErrors.pax}  // Show error if validation failed for this field
                                helperText={formErrors.pax}  // Show error message
                                type="number"
                                variant="outlined"
                                size="small"
                                multiline
                                rows={1.5}
                                InputProps={{
                                    style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Order Note"
                                name="orderNote"
                                value={formData.orderNote}
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                                placeholder="Suggestions..."
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    style: { borderRadius: '8px', backgroundColor: '#f9f9f9' },
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={handleWhatsAppOrder}
                    >
                        Order on WhatsApp
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={clearFormCookies}
                    >
                        Clear Form
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default Cart;






